import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/* ───────────────────────────────────────────
   Animation Variants – dramatic scroll reveals
   ─────────────────────────────────────────── */

const variants = {
  fadeUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -30 },
    visible: { opacity: 1, y: 0 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
  },
  scaleUp: {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1 },
  },
  scaleRotate: {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  },
  slideUp: {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0 },
  },
  blur: {
    hidden: { opacity: 0, filter: 'blur(6px)' },
    visible: { opacity: 1, filter: 'blur(0px)' },
  },
  clipUp: {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 },
  },
  subtle: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
};

function useSafeInView(ref, once = true) {
  return useInView(ref, {
    once,
    margin: '-80px 0px -80px 0px',
    amount: 0.15,
  });
}

/* ───────────────────────────────────────────
   Reveal – single element with variant
   ─────────────────────────────────────────── */
export const Reveal = ({
  children,
  className = '',
  delay = 0,
  variant = 'fadeUp',
  duration = 0.65,
  once = true,
}) => {
  const ref = useRef(null);
  const shouldShow = useSafeInView(ref, once);
  const v = variants[variant] || variants.fadeUp;

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={shouldShow ? 'visible' : 'hidden'}
      variants={v}
      transition={{
        duration,
        delay: delay / 1000,
        ease: [0.22, 1, 0.36, 1],
      }}
      data-testid="reveal-section"
    >
      {children}
    </motion.div>
  );
};

/* ───────────────────────────────────────────
   StaggerContainer – staggers children
   ─────────────────────────────────────────── */
export const StaggerContainer = ({
  children,
  className = '',
  stagger = 0.1,
  delay = 0,
  once = true,
}) => {
  const ref = useRef(null);
  const shouldShow = useSafeInView(ref, once);

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={shouldShow ? 'visible' : 'hidden'}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: stagger,
            delayChildren: delay / 1000,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

/* ───────────────────────────────────────────
   StaggerItem – child that inherits stagger
   ─────────────────────────────────────────── */
export const StaggerItem = ({
  children,
  className = '',
  variant = 'fadeUp',
  duration = 0.55,
}) => {
  const v = variants[variant] || variants.fadeUp;
  return (
    <motion.div
      className={className}
      variants={v}
      transition={{
        duration,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {children}
    </motion.div>
  );
};

/* ───────────────────────────────────────────
   HoverCard – card with rich hover effect
   ─────────────────────────────────────────── */
export const HoverCard = ({ children, className = '', scale = 1.02 }) => {
  return (
    <motion.div
      className={className}
      style={{ height: '100%' }}
      whileHover={{
        y: -6,
        scale,
        boxShadow: '0 20px 40px -20px rgba(0,0,0,0.2)',
      }}
      whileTap={{ scale: 0.98 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
      }}
    >
      {children}
    </motion.div>
  );
};

/* ───────────────────────────────────────────
   AnimatedCounter – count up number
   ─────────────────────────────────────────── */
export const AnimatedCounter = ({ target, suffix = '', prefix = '', duration = 2, className = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = parseInt(target);
    if (end <= 0) return;
    const stepTime = Math.max((duration * 1000) / end, 30);
    const timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start >= end) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [isInView, target, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

/* ───────────────────────────────────────────
   FloatingElement – subtle float animation
   ─────────────────────────────────────────── */
export const FloatingElement = ({ children, className = '', yOffset = 8, duration = 3 }) => {
  return (
    <motion.div
      className={className}
      animate={{
        y: [-yOffset, yOffset, -yOffset],
      }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    >
      {children}
    </motion.div>
  );
};

/* ───────────────────────────────────────────
   PulseGlow – subtle pulsing glow behind element
   ─────────────────────────────────────────── */
export const PulseGlow = ({ children, className = '', color = '#1F6E7A' }) => {
  return (
    <motion.div
      className={`relative ${className}`}
      whileHover="hover"
    >
      <motion.div
        className="absolute inset-0 rounded-full blur-xl opacity-0"
        style={{ backgroundColor: color }}
        variants={{
          hover: { opacity: 0.15, scale: 1.2 },
        }}
        transition={{ duration: 0.3 }}
      />
      <div className="relative">{children}</div>
    </motion.div>
  );
};
